import { ref, type Ref } from "vue";
import {
  uploadsAPI,
  type RestaurantAsset,
  type UploadsAPI,
} from "@app/data/api/uploads";

/**
 * The restaurant's uploaded images, for any asset picker.
 *
 * Backed by the existing `GET /api/v2/uploads` route, which returns
 * `restaurant_assets` rows with their resolved R2 URL. Loaded once per page and
 * shared (module-level cache), since several pickers pick from the same list.
 */
type AssetLibraryState = {
  assets: Ref<RestaurantAsset[]>;
  loading: Ref<boolean>;
  error: Ref<Error | null>;
  loaded: boolean;
};

const libraries = new WeakMap<UploadsAPI, AssetLibraryState>();

export const useAssetLibrary = (api: UploadsAPI = uploadsAPI) => {
  let state = libraries.get(api);
  if (!state) {
    state = {
      assets: ref<RestaurantAsset[]>([]),
      loading: ref(false),
      error: ref<Error | null>(null),
      loaded: false,
    };
    libraries.set(api, state);
  }

  const load = async (force = false) => {
    if (state!.loaded && !force) return;
    state!.loading.value = true;
    state!.error.value = null;
    try {
      state!.assets.value = await api.getAssets();
      state!.loaded = true;
    } catch (e) {
      state!.error.value = e instanceof Error ? e : new Error(String(e));
    } finally {
      state!.loading.value = false;
    }
  };

  return {
    assets: state.assets,
    loading: state.loading,
    error: state.error,
    load,
  };
};
